import type { FC } from 'react';

import type { BoxProps } from '../box/box.tsx';
import { Box } from '../box/box.tsx';

import { cnFlex } from './flex.constants.ts';

import './flex.css';

import './_align/flex_align_baseline.css';
import './_align/flex_align_center.css';
import './_align/flex_align_end.css';
import './_align/flex_align_start.css';
import './_align/flex_align_stretch.css';
import './_direction/flex_direction_column-reverse.css';
import './_direction/flex_direction_column.css';
import './_direction/flex_direction_row-reverse.css';
import './_direction/flex_direction_row.css';
import './_display/flex_display_flex.css';
import './_display/flex_display_inline-flex.css';
import './_display/flex_display_none.css';
import './_gap/flex_gap_0.css';
import './_gap/flex_gap_1.css';
import './_gap/flex_gap_2.css';
import './_gap/flex_gap_3.css';
import './_gap/flex_gap_4.css';
import './_gap/flex_gap_5.css';
import './_gap/flex_gap_6.css';
import './_gap/flex_gap_7.css';
import './_gap/flex_gap_8.css';
import './_gap/flex_gap_9.css';
import './_gapx/flex_gapx_0.css';
import './_gapx/flex_gapx_1.css';
import './_gapx/flex_gapx_2.css';
import './_gapx/flex_gapx_3.css';
import './_gapx/flex_gapx_4.css';
import './_gapx/flex_gapx_5.css';
import './_gapx/flex_gapx_6.css';
import './_gapx/flex_gapx_7.css';
import './_gapx/flex_gapx_8.css';
import './_gapx/flex_gapx_9.css';
import './_gapy/flex_gapy_0.css';
import './_gapy/flex_gapy_1.css';
import './_gapy/flex_gapy_2.css';
import './_gapy/flex_gapy_3.css';
import './_gapy/flex_gapy_4.css';
import './_gapy/flex_gapy_5.css';
import './_gapy/flex_gapy_6.css';
import './_gapy/flex_gapy_7.css';
import './_gapy/flex_gapy_8.css';
import './_gapy/flex_gapy_9.css';
import './_justify/flex_justify_between.css';
import './_justify/flex_justify_center.css';
import './_justify/flex_justify_end.css';
import './_justify/flex_justify_start.css';
import './_wrap/flex_wrap_nowrap.css';
import './_wrap/flex_wrap_wrap-reverse.css';
import './_wrap/flex_wrap_wrap.css';

type GapScale = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';

export type FlexProps = BoxProps & {
    /** Sets the CSS `display` property. Defaults to `"flex"`. */
    display?: 'none' | 'inline-flex' | 'flex';
    /** Sets the CSS `flex-direction` property. */
    direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
    /** Sets the CSS `align-items` property. */
    align?: 'start' | 'center' | 'end' | 'baseline' | 'stretch';
    /** Sets the CSS `justify-content` property. */
    justify?: 'start' | 'center' | 'end' | 'between';
    /** Sets the CSS `flex-wrap` property. */
    wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
    /** Sets the CSS `gap` property using the space scale. */
    gap?: GapScale;
    /** Sets the CSS `column-gap` property using the space scale. */
    gapX?: GapScale;
    /** Sets the CSS `row-gap` property using the space scale. */
    gapY?: GapScale;
};

/**
 * Flexbox layout primitive. Inherits margin, padding, and layout props from `Box`.
 *
 * Basic usage:
 * ```tsx
 * <Flex gap="2" align="center">
 *   <Box />
 *   <Box />
 * </Flex>
 * ```
 *
 * Column layout with stretching:
 * ```tsx
 * <Flex direction="column" align="stretch">...</Flex>
 * ```
 *
 * Inline-flex span:
 * ```tsx
 * <Flex as="span" display="inline-flex">...</Flex>
 * ```
 *
 * Wrapping a custom child:
 * ```tsx
 * <Flex asChild>
 *   <section>...</section>
 * </Flex>
 * ```
 */
export const Flex: FC<FlexProps> = ({
    className,
    display,
    direction,
    align,
    justify,
    wrap,
    gap,
    gapX,
    gapY,
    ...props
}) => (
    <Box
        {...props}
        className={cnFlex({ display, direction, align, justify, wrap, gap, gapx: gapX, gapy: gapY }, [className])}
    />
);
